import { useState } from 'react';
import { Media } from '@weave/schema-gen-ts/dist/schemas/messaging/media/public/v1/service.pb';
import { LogoSection as LogoSectionType } from '@weave/schema-gen-ts/dist/schemas/messaging/shared/v1/composer.pb';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { useModalControl } from '@frontend/design-system';
import { useComposerSection } from '../../hooks';
import { ComposerSection } from '../../types';
import { convertMediaToImage, getFrameStyle } from '../../utils';
import { LogoOutput } from '../outputs';
import { LogoSettings } from '../settings';
import { LogoTool } from '../tools';
import { BaseComposerSection } from './floating-toolbar';
import {
  createImagesWithDimensions,
  ImageWithDimensions,
  JustifiedLayout,
  newImageWithDimensions,
  removeDimensions,
  SelectImageModal,
} from './image-elements';

export const LogoSection: ComposerSection<LogoSectionType> = ({ corners, frame, height, image }) => {
  const { t } = useTranslation('content-composer');
  const [logoWithDimensions, setLogoWithDimensions] = useState(() =>
    createImagesWithDimensions(1, image ? [image] : [])
  );
  const [isLogoSelected, setIsLogoSelected] = useState(false);
  const { setProps } = useComposerSection<LogoSectionType>();
  const { modalProps, triggerProps } = useModalControl();

  const loadLogo = (imageIndex: number, width: number, height: number) => {
    setLogoWithDimensions((previousLogoWithDimensions) => {
      const updatedLogoWithDimensions = [...previousLogoWithDimensions];
      updatedLogoWithDimensions[imageIndex] = {
        ...newImageWithDimensions(updatedLogoWithDimensions[imageIndex]),
        width,
        height,
      };
      return updatedLogoWithDimensions;
    });
  };

  const saveLogo = (updatedLogoWithDimensions: ImageWithDimensions) => {
    const updatedLogo = removeDimensions(updatedLogoWithDimensions);
    setProps((props) => (props.image = updatedLogo));
    setLogoWithDimensions([updatedLogoWithDimensions]);
  };

  const deselectLogo = () => {
    setIsLogoSelected(false);
  };

  const selectLogo = () => {
    setIsLogoSelected(true);
  };

  const removeLogo = () => {
    deselectLogo();
    saveLogo(newImageWithDimensions());
  };

  const startReplaceLogo = () => {
    selectLogo();
    triggerProps.onClick();
  };

  const finishReplaceLogo = (media?: Media) => {
    if (!media) return;
    const updatedLogoWithDimensions = newImageWithDimensions(convertMediaToImage(media));
    saveLogo(updatedLogoWithDimensions);
  };

  return (
    <BaseComposerSection title={t('Logo')} css={getFrameStyle(frame)} canCopy={false} onClick={deselectLogo}>
      <JustifiedLayout
        alignment={frame?.alignment}
        corners={corners}
        height={height}
        images={logoWithDimensions}
        onLoad={loadLogo}
        onRemove={removeLogo}
        onReplace={startReplaceLogo}
        onSelect={selectLogo}
        selectedImageIndex={isLogoSelected ? 0 : undefined}
      />
      <SelectImageModal modalProps={modalProps} onSelect={finishReplaceLogo} />
    </BaseComposerSection>
  );
};

LogoSection.craft = {
  props: {
    frame: {
      alignment: 'center',
      backgroundColor: theme.colors.neutral5.toUpperCase(),
      paddingBottom: '24px',
      paddingLeft: '24px',
      paddingRight: '24px',
      paddingTop: '24px',
    },
    height: '64px',
    sectionType: 'LogoSection',
  },
  related: {
    output: LogoOutput,
    settings: LogoSettings,
    tool: LogoTool,
  },
};
